import { Box } from '@modusign/design-system';
import { useRequester } from '@modusign/react-signing';
import { useUIState } from '@modusign/react-signing/setup';
import { useRef } from 'react';
import { useEvent, useKeyPressEvent } from 'react-use';

import noop from '@libs/noop';

import { RequesterCheckboxField } from '@app/features/apply-react-signing/components/FieldRenderer/Setup/components/RequesterFields/RequesterCheckboxField';
import { RequesterImageField } from '@app/features/apply-react-signing/components/FieldRenderer/Setup/components/RequesterFields/RequesterImageField';
import { RequesterTextField } from '@app/features/apply-react-signing/components/FieldRenderer/Setup/components/RequesterFields/RequesterTextField';
import { Viewport } from '@app/features/apply-react-signing/components/FieldRenderer/types';
import { getPageInField } from '@app/features/apply-react-signing/libs/getFiledNodePage';
import useMouseClientPosition from '@app/hooks/useMouseClientPosition';

interface RequesterMousePlaceholderProps {
  scale: number;
  viewports: Viewport[];
}

export default function RequesterMousePlaceholder({
  scale,
  viewports,
}: RequesterMousePlaceholderProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { clientX, clientY } = useMouseClientPosition(ref);
  const { requester } = useRequester();
  const { ui, resetInsertingField } = useUIState();

  let mousePlaceholderElement: React.ReactNode | null;

  useKeyPressEvent('Escape', () => {
    if (ui.insertingField && mousePlaceholderElement) {
      resetInsertingField();
    }
  });
  useEvent('contextmenu', (event: MouseEvent) => {
    if (ui.insertingField && mousePlaceholderElement) {
      // 브라우저에서 마우스 우클릭하면 나오는 컨텍스트 메뉴가 안나오게 합니다.
      event.preventDefault();
      resetInsertingField();
    }
  });

  if (!ui.insertingField || ui.insertingField.participantId !== requester?.id) {
    return null;
  }

  const page = getPageInField(ui.insertingField.field);
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const viewport = viewports.find((viewport) => viewport.pageNumber === page)!;

  if (ui.insertingField.field.type === 'CHECKBOX') {
    mousePlaceholderElement = (
      <RequesterCheckboxField
        field={ui.insertingField.field}
        isDataLabelDuplicated={false}
        isDataLabelEditable
        viewport={viewport}
        /**
         * (@Juyeong-Byeon)
         * 적용될 field를 그대로 mouse placeholder에서 보여주어야 해서 Field를 그대로 이용합니다.
         * 그러나 이벤트 핸들러에 따른 처리는 불필요해서 noop으로 모두 전달해둡니다.
         * @see https://github.com/modusign/modusign-client/pull/8407#discussion_r1173208550
         */
        onCheckedChange={noop}
        onDataLabelChange={noop}
        onFieldRemove={noop}
        onPointChange={noop}
        onSizeChange={noop}
      />
    );
  } else if (ui.insertingField.field.type === 'IMAGE') {
    mousePlaceholderElement = (
      <RequesterImageField
        field={ui.insertingField.field}
        isDataLabelDuplicated={false}
        isDataLabelEditable
        viewport={viewport}
        /**
         * (@Juyeong-Byeon)
         * 적용될 field를 그대로 mouse placeholder에서 보여주어야 해서 Field를 그대로 이용합니다.
         * 그러나 이벤트 핸들러에 따른 처리는 불필요해서 noop으로 모두 전달해둡니다.
         * @see https://github.com/modusign/modusign-client/pull/8407#discussion_r1173208550
         */
        onDataLabelChange={noop}
        onFieldRemove={noop}
        onImageChange={noop}
        onPointChange={noop}
        onSizeChange={noop}
      />
    );
  } else if (ui.insertingField.field.type === 'TEXT') {
    mousePlaceholderElement = (
      <RequesterTextField
        field={ui.insertingField.field}
        isDataLabelDuplicated={false}
        isDataLabelEditable
        scale={scale}
        viewport={viewport}
        /**
         * (@Juyeong-Byeon)
         * 적용될 field를 그대로 mouse placeholder에서 보여주어야 해서 Field를 그대로 이용합니다.
         * 그러나 이벤트 핸들러에 따른 처리는 불필요해서 noop으로 모두 전달해둡니다.
         *
         * @see https://github.com/modusign/modusign-client/pull/8407#discussion_r1173208550
         */
        onDataLabelChange={noop}
        onFieldRemove={noop}
        onFontAlignChange={noop}
        onFontSizeChange={noop}
        onFontStyleChange={noop}
        onPointChange={noop}
        onSizeChange={noop}
        onTextChange={noop}
      />
    );
  } else {
    mousePlaceholderElement = null;
  }

  return (
    <Box
      ref={ref}
      /**
       * @see https://modusign.slack.com/archives/CBT88M56C/p1671091123702209
       */
      style={{
        transform: `translate(${clientX}px, ${clientY}px)`,
      }}
      sx={{
        cursor: 'default',
        /**
         * useMount로 마우스 포인터 위치를 가져올 때
         * mousemove 이벤트가 발생하지 않으면 좌표가 (0,0) 값이라
         * 마우스를 움직이기 전까지 화면 왼쪽 위 구석에 나타나는 문제가 발생합니다.
         * 마우스 커서 좌표를 추적하기 위해 컴포넌트 마운트 상태, 렌더링을 유지하고
         * css display 속성으로 숨김처리하게 구현했습니다.
         */
        display: !ui.insertingField || !mousePlaceholderElement ? 'none' : undefined,
        left: 0,
        position: 'fixed',
        top: 0,
        zIndex: 501, // $mouse-marker-z-index
        pointerEvents: 'none',
      }}
    >
      {mousePlaceholderElement}
    </Box>
  );
}
